import { useCallback, useMemo, useState } from "react";

import type {
  CreateFastMenuActionItemRequest,
  FastMenuActionItemDto,
  HttpResponse,
  UpdateFastMenuActionItemRequest,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { Asterisk01Icon } from "@/components/Icons/Asterisk01Icon";
import { Building02Icon } from "@/components/Icons/Building02Icon";
import { CalendarHeart02Icon } from "@/components/Icons/CalendarHeart02Icon";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Globe02Icon } from "@/components/Icons/Globe02Icon";
import { MessageQuestionSquareIcon } from "@/components/Icons/MessageQuestionSquareIcon";
import { Tool02Icon } from "@/components/Icons/Tool02Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Select } from "@/design-system/Select";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { canManageProjectManagement } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useTranslation } from "@/translations";

import { CustomUrlModal } from "./components/CustomUrlModal";
import { MaintainerModal } from "./components/MaintainerModal";
import type { PlusButtonOptionTypes, TicketOptionType, TicketServiceLabels } from "./constants";
import { mandatoryOptions, menuOptions, ticketOptions } from "./constants";

export interface PlusButtonOptionsContentProps {
  buttonOptions: FastMenuActionItemDto[];
  isZiezodanProject: boolean;
  createOption: (payload: CreateFastMenuActionItemRequest) => Promise<HttpResponse<FastMenuActionItemDto, any>>;
  isCreatingOption: boolean;
  editOption({
    id,
    payload,
  }: {
    id: string;
    payload: UpdateFastMenuActionItemRequest;
  }): Promise<HttpResponse<FastMenuActionItemDto, any>>;
  isEditingOption: boolean;
  deleteOption: (id: string) => Promise<HttpResponse<void, any>>;
  isDeletingOption: boolean;
  isLoading: boolean;
}
export function PlusButtonOptionsContent({
  buttonOptions,
  isZiezodanProject,
  createOption,
  isCreatingOption,
  editOption,
  isEditingOption,
  deleteOption,
  isDeletingOption,
  isLoading,
}: PlusButtonOptionsContentProps): React.ReactNode {
  const { t } = useTranslation();

  function getTicketOptionId(translationLabel: string | null | undefined): TicketOptionType["id"] {
    if (!translationLabel || translationLabel === "create.ticket") {
      return "ticket";
    }
    if (translationLabel === "create.ticket.maintainer") {
      return "ticketWithMaintainer";
    }
    if (translationLabel === "create.service.request") {
      return "serviceRequest";
    }

    return "serviceRequestWithMaintainer";
  }
  const sessionUser = useSessionUser();
  const [selectedOption, setSelectedOption] = useState<FastMenuActionItemDto | null>(null);
  const [isMaintainerModalOpened, maintainerModalHandler] = useBool(false);
  const [isCustomUrlModalOpened, customUrlModalHandler] = useBool(false);
  const [isDeleteModalOpen, deleteModalHandler] = useBool(false);
  const hasPermission = usePermission();
  const getIcon = useCallback(
    (type: PlusButtonOptionTypes) => {
      switch (type) {
        case "createInform":
          return <Edit05Icon size={20} data-testid="inform-button-icon" />;
        case "createHelp":
          return <MessageQuestionSquareIcon size={20} data-testid="help-button-icon" />;
        case "createMarketplace":
          return <Building02Icon size={20} data-testid="marketplace-button-icon" />;
        case "createEvent":
          return <CalendarIcon size={20} data-testid="event-button-icon" />;
        case "createTicket":
          return isZiezodanProject ? (
            <Asterisk01Icon size={20} data-testid="ticket-button-icon" />
          ) : (
            <Tool02Icon size={20} data-testid="ticket-button-icon" />
          );
        case "createServiceRequest":
          return <Tool02Icon size={20} data-testid="ticket-button-icon" />;
        case "openZiezodan":
          return isZiezodanProject ? (
            <Tool02Icon size={20} data-testid="ziezodan-button-icon" />
          ) : (
            <Asterisk01Icon size={20} data-testid="ziezodan-button-icon" />
          );
        case "openExternalUrl":
        case "openDynamicExternalUrl":
          return <Globe02Icon size={20} data-testid="external-url-button-icon" />;
        case "bookAsset":
          return <CalendarHeart02Icon size={20} data-testid="book-asset-button-icon" />;
      }
    },
    [isZiezodanProject],
  );
  const getDynamicExternalUrlLabel = useCallback(
    (dynamicExternalUrlType: FastMenuActionItemDto["dynamicExternalUrlType"]): string => {
      switch (dynamicExternalUrlType) {
        case "healthDistrict":
          return t("page.plus-button-links.labels.dynamic-custom-url.health-district");
        default:
          return "";
      }
    },
    [t],
  );
  const getLabel = useCallback(
    (translationLabel: string): string => {
      switch (translationLabel) {
        case "createInform":
          return t("page.plus-button-links.labels.inform");
        case "createHelp":
          return t("page.plus-button-links.labels.help");
        case "createMarketplace":
          return t("page.plus-button-links.labels.marketplace");
        case "createEvent":
          return t("page.plus-button-links.labels.event");
        case "createTicket":
          return t("page.plus-button-links.labels.ticket");
        case "createServiceRequest":
          return t("page.plus-button-links.labels.service");
        case "openZiezodan":
          return t("page.plus-button-links.labels.ziezodan");
        case "openExternalUrl":
          return t("page.plus-button-links.labels.custom-url");
        case "openDynamicExternalUrl":
          return t("page.plus-button-links.labels.dynamic-custom-url");
        case "bookAsset":
          return t("page.plus-button-links.labels.book-asset");
        default:
          return "";
      }
    },
    [t],
  );
  const getTypeLabel = useCallback(
    (type: PlusButtonOptionTypes): string => {
      switch (type) {
        case "createInform":
          return t("page.plus-button-links.types.inform");
        case "createHelp":
          return t("page.plus-button-links.types.help");
        case "createMarketplace":
          return t("page.plus-button-links.types.marketplace");
        case "createEvent":
          return t("page.plus-button-links.types.event");
        case "createTicket":
          return t("page.plus-button-links.types.ticket");
        case "createServiceRequest":
          return t("page.plus-button-links.types.service");
        case "openZiezodan":
          return t("page.plus-button-links.types.ziezodan");
        case "openExternalUrl":
          return t("page.plus-button-links.types.custom-url");
        case "openDynamicExternalUrl":
          return t("page.plus-button-links.types.dynamic-custom-url");
        case "bookAsset":
          return t("page.plus-button-links.types.book-asset");
        default:
          return "";
      }
    },
    [t],
  );
  const getTicketLabel = useCallback(
    (serviceLabel: TicketServiceLabels): string => {
      switch (serviceLabel) {
        case "create.ticket.maintainer":
          return t("page.plus-button-links.labels.ticket.create-for-maintainer");
        case "create.service.request":
          return t("page.plus-button-links.labels.service");
        case "create.service.request.maintainer":
          return t("page.plus-button-links.labels.service.ask-maintainer");
        case "create.ticket":
        default:
          return t("page.plus-button-links.labels.ticket");
      }
    },
    [t],
  );
  const getMenuLabel = useCallback(
    (type: PlusButtonOptionTypes): string => {
      switch (type) {
        case "createMarketplace":
          return t("page.plus-button-links.options.marketplace");
        case "createEvent":
          return t("page.plus-button-links.options.event");
        case "createTicket":
          return t("page.plus-button-links.options.service");
        case "openZiezodan":
          return t("page.plus-button-links.options.ziezodan");
        case "openExternalUrl":
          return t("page.plus-button-links.options.custom-url");
        case "openDynamicExternalUrl":
          return t("page.plus-button-links.options.dynamic-custom-url");
        case "bookAsset":
          return t("page.plus-button-links.options.book-asset");
        default:
          return "";
      }
    },
    [t],
  );
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<FastMenuActionItemDto>();

    return [
      helper.accessor("type", {
        id: "icon",
        header: t("page.plus-button-links.table.column.icon"),
        cell: (cell) => <span className="flex w-full justify-center">{getIcon(cell.getValue())}</span>,
        size: 64,
        enableResizing: false,
      }),
      helper.accessor("type", {
        id: "name",
        header: t("page.plus-button-links.table.column.label"),
        cell: (cell) => {
          const value = cell.getValue();

          if (!hasPermission(canManageProjectManagement)) {
            return <span className="truncate text-caption">{getLabel(value)}</span>;
          }

          switch (cell.row.original.type) {
            case "createTicket":
            case "createServiceRequest":
              return (
                <Inline gap="sm">
                  <Select<TicketOptionType>
                    selected={{
                      id: getTicketOptionId(cell.row.original.translationLabel),
                      type: cell.row.original.type,
                      label: cell.row.original.translationLabel as TicketServiceLabels,
                    }}
                    items={ticketOptions}
                    onChange={(x) => {
                      if (x.label === "create.ticket.maintainer" || x.label === "create.service.request.maintainer") {
                        setSelectedOption({
                          ...cell.row.original,
                          translationLabel: x.label,
                        });
                        maintainerModalHandler.setTrue();
                      } else {
                        void editOption({
                          id: cell.row.original.id,
                          payload: {
                            type: x.type,
                            selectedLabel: x.label,
                          },
                        });
                      }
                    }}
                    keySelector={(x) => x.id}
                    renderOption={(x) => getTicketLabel(x.label)}
                    usePortal
                  />
                  {(cell.row.original.translationLabel === "create.service.request.maintainer" ||
                    cell.row.original.translationLabel === "create.ticket.maintainer") && (
                    <Button
                      variant="ghostPrimary"
                      onClick={() => {
                        setSelectedOption(cell.row.original);
                        maintainerModalHandler.setTrue();
                      }}
                    >
                      <span className="text-caption">{cell.row.original.maintainer}</span>
                      <Edit05Icon />
                    </Button>
                  )}
                </Inline>
              );
            case "openExternalUrl":
              return (
                <Button
                  variant="ghostPrimary"
                  onClick={() => {
                    setSelectedOption(cell.row.original);
                    customUrlModalHandler.setTrue();
                  }}
                  size="sm"
                >
                  {cell.row.original.translations?.find(
                    (translation) => translation.language === sessionUser.language.id,
                  )?.text ?? getLabel(value)}
                </Button>
              );
            case "openDynamicExternalUrl":
              return getDynamicExternalUrlLabel(cell.row.original.dynamicExternalUrlType);
            default:
              return getLabel(value);
          }
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("type", {
        header: t("page.plus-button-links.table.column.type"),
        cell: (cell) => getTypeLabel(cell.getValue()),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("type", {
        id: "actions",
        header: "",
        cell: (cell) => {
          if (!hasPermission(canManageProjectManagement)) {
            return null;
          }

          const value = cell.getValue();

          const menuItems: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              "data-testid": "context-menu-edit-btn",
              icon: <Edit05Icon />,
              callbackFn: () => {
                if (cell.row.original.type === "openZiezodan") return;
                setSelectedOption(cell.row.original);
                customUrlModalHandler.setTrue();
              },
              isHidden: value !== "openExternalUrl" || !cell.row.original.url,
              isDisabled: cell.row.original.type === "openZiezodan",
              value: "edit",
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              "data-testid": "context-menu-delete-btn",
              icon: <Trash01Icon />,
              callbackFn: () => {
                setSelectedOption(cell.row.original);
                deleteModalHandler.setTrue();
              },
              isDisabled: isEditingOption,
            },
          ];

          if (mandatoryOptions.includes(value)) return null;

          return <ContextMenu items={menuItems} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [
    t,
    getIcon,
    hasPermission,
    getLabel,
    getDynamicExternalUrlLabel,
    maintainerModalHandler,
    editOption,
    getTicketLabel,
    customUrlModalHandler,
    sessionUser.language.id,
    getTypeLabel,
    isEditingOption,
    deleteModalHandler,
  ]);
  const tableInstance = useResizableTable(TABLE_KEYS.PLUS_BUTTON_OPTIONS, {
    columns,
    data: buttonOptions,
    enableSorting: false,
  });
  const menuActions: ContextMenuItem[] = menuOptions.map((menuOption) => {
    return {
      labels: {
        default: getMenuLabel(menuOption),
      },
      value: menuOption,
      icon: getIcon(menuOption),
      callbackFn: async () => {
        switch (menuOption) {
          case "openExternalUrl":
            customUrlModalHandler.setTrue();
            break;
          case "createTicket":
            await createOption({
              type: menuOption,
              selectedLabel: "create.ticket",
            });
            break;
          default:
            await createOption({
              type: menuOption,
            });
        }
      },
      isDisabled:
        buttonOptions.some(
          (buttonOption) =>
            (buttonOption.type === menuOption && menuOption !== "openExternalUrl") ||
            (buttonOption.type === "createServiceRequest" && menuOption === "createTicket") ||
            (buttonOption.type === "createTicket" && menuOption === "createServiceRequest") ||
            (buttonOption.type === "bookAsset" && menuOption === "bookAsset"),
        ) ||
        (menuOption === "openZiezodan" && !isZiezodanProject),
    };
  });

  const handleAddCustomUrlOption = useCallback(
    async ({
      id,
      label,
      url,
      useType,
    }: {
      id: string;
      label: string;
      url: string;
      useType: CreateFastMenuActionItemRequest["useType"];
    }) => {
      const payload: CreateFastMenuActionItemRequest = {
        type: "openExternalUrl",
        label,
        url,
        useType,
      };

      if (id) {
        await editOption({
          id: id,
          payload,
        });
      } else {
        await createOption(payload);
      }

      setSelectedOption(null);
      customUrlModalHandler.setFalse();
    },
    [createOption, customUrlModalHandler, editOption],
  );

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.plus-button-links.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.plus-button-links.title"),
            },
          ]}
        />
      }
      actions={
        hasPermission(canManageProjectManagement) && (
          <ContextMenu
            triggerComponent={
              <Button
                data-testid="plus-button-add-option-btn"
                disabled={buttonOptions.length === 7 || isCreatingOption}
                title={buttonOptions.length === 7 ? t("page.plus-button-links.add-option-disabled") : undefined}
              >
                {t("page.plus-button-links.add-option")}
              </Button>
            }
            items={menuActions}
          />
        )
      }
    >
      <Table table={tableInstance} isLoading={isLoading} />
      <MaintainerModal
        key={`maintainer_modal_${selectedOption?.id}`}
        isOpened={isMaintainerModalOpened}
        isLoading={isCreatingOption || isEditingOption}
        onOpenChange={(state) => {
          if (!state) {
            setSelectedOption(null);
          }
          maintainerModalHandler.set(state);
        }}
        onSubmit={async (id: string | undefined, maintainer: string) => {
          if (!selectedOption) {
            return;
          }

          if (id) {
            await editOption({
              id: id,
              payload: {
                type: selectedOption.type,
                maintainer: maintainer,
                selectedLabel: selectedOption.translationLabel,
              },
            });
          } else {
            await createOption({
              type: selectedOption.type,
              maintainer: maintainer,
            });
          }
          setSelectedOption(null);
          maintainerModalHandler.setFalse();
        }}
        defaultValues={
          selectedOption
            ? {
                id: selectedOption.id,
                maintainer: selectedOption.maintainer!,
              }
            : undefined
        }
      />
      <CustomUrlModal
        key={`custom_url_modal_${selectedOption?.id}`}
        isOpened={isCustomUrlModalOpened}
        isLoading={isCreatingOption || isEditingOption}
        onOpenChange={(state) => {
          if (!state) {
            setSelectedOption(null);
          }

          customUrlModalHandler.set(state);
        }}
        onSubmit={handleAddCustomUrlOption}
        defaultValues={
          selectedOption
            ? {
                id: selectedOption.id,
                url: selectedOption.url!,
                label: selectedOption.text ?? "",
                useType: selectedOption.useType ?? "webPage",
              }
            : undefined
        }
      />
      <ConfirmModal
        title={t("page.plus-button-links.delete.modal.title")}
        description={t("page.plus-button-links.delete.modal.description")}
        isLoading={isDeletingOption}
        tone="error"
        onReject={() => {
          setSelectedOption(null);
          deleteModalHandler.setFalse();
        }}
        rejectBtnProps={{
          "data-testid": "plus-button-options-delete-modal-cancel",
        }}
        onOpenChange={deleteModalHandler.set}
        onResolve={async () => {
          if (selectedOption !== null) {
            await deleteOption(selectedOption?.id);
          }
          deleteModalHandler.setFalse();
          setSelectedOption(null);
        }}
        resolveBtnProps={{
          text: t("common.action.delete"),
          "data-testid": "plus-button-options-delete-modal-delete",
        }}
        isOpened={isDeleteModalOpen}
        shouldCloseOnEsc
      />
    </DocumentPage>
  );
}
